import { type ReactNode } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Button } from './Button';

// Cortex EmptyState (DESIGN_LANGUAGE_SPEC §25, §27: "helpful empty states with CTAs").
// A centered icon + title + description with an optional call-to-action Button.

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  /** Icon element (e.g. a lucide icon) shown above the title. */
  icon?: ReactNode | undefined;
  title: string;
  description?: string | undefined;
  /** Optional CTA rendered as a primary Button. */
  action?: EmptyStateAction | undefined;
  className?: string | undefined;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div
      className={`flex flex-col items-center justify-center py-16 text-center ${
        className ?? ''
      }`}
    >
      {icon && (
        <div
          className={`mb-4 opacity-50 ${
            isDark ? 'text-white/40' : 'text-slate-400'
          }`}
          aria-hidden="true"
        >
          {icon}
        </div>
      )}
      <h3
        className={`mb-2 text-lg font-semibold ${
          isDark ? 'text-white/60' : 'text-slate-600'
        }`}
      >
        {title}
      </h3>
      {description && (
        <p
          className={`mb-6 max-w-sm text-sm ${
            isDark ? 'text-white/40' : 'text-slate-400'
          }`}
        >
          {description}
        </p>
      )}
      {action && (
        <Button variant="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}
